import axios from 'axios';
import { showErrorToast } from './Toast';
import { getAccessToken, clearAccessToken } from './authToken';

/**
 * Pre-configured Axios instance for all API requests.
 * Automatically attaches JWT auth headers and handles 401 token expiry.
 * @see interceptors below for auth and error-handling logic.
 */
// 30 s default timeout — Codex WARNING fix. Calls without a timeout could
// hang indefinitely on stalled networks or unresponsive backend processes.
// Per-call overrides are still possible via { timeout: <ms> } in the request
// config, e.g. for slow uploads.
const DEFAULT_TIMEOUT_MS = 30000;

const ApiCall = axios.create({
  baseURL: process.env.REACT_APP_URL || "/api",  // fallback so API calls keep the /api prefix even if env unset (Caddy proxies /api*)
  timeout: DEFAULT_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor — attach JWT via the centralised authToken module.
// When this codebase migrates to httpOnly cookies, this entire interceptor
// can be deleted and ApiCall just gets `withCredentials: true` instead.
ApiCall.interceptors.request.use(
  (config) => {
    const accessToken = getAccessToken();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor — handle expired/invalid token globally
ApiCall.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      // Token expired or invalid — clear storage and redirect to login
      clearAccessToken();
      // Dispatch logout via store to clear Redux state
      try {
        const { store } = require('../store/store');
        const { logout } = require('../feature/userSlice');
        store.dispatch(logout());
      } catch (_) { /* store may not be initialised yet */ }
      showErrorToast('Your session has expired. Please log in again.');
      // Small delay so the toast is visible before the redirect
      setTimeout(() => { window.location.href = '/login'; }, 1500);
    }
    return Promise.reject(error);
  }
);

export default ApiCall;
